import { observe, Lambda, IObservableArray } from 'mobx';

import type { UiStore } from './ui-store';
import type { EventsStore } from '../events/events-store';
import type { HttpExchange } from '../http/http-exchange';
import type { FilterSet } from '../filters/search-filters';

import { DesktopApi } from '../../services/desktop-api';
import { logError } from '../../errors';

const UTF8Decoder = new TextDecoder('utf8', { fatal: true });

// Prevents double-writes if the same exchange happens to be pushed / spliced twice.
// Also stops us re-saving an exchange whose body only just finished decoding.
const savedIds = new Set<string>();

const filenameSafe = (s: string, max = 40) =>
    s.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, max) || 'unknown';

const matchesFilter = (exchange: HttpExchange, filterSet: FilterSet): boolean => {
    if (filterSet.length === 0) return true;
    return filterSet.every(f => f.matches(exchange));
};

async function saveExchange(folder: string, exchange: HttpExchange): Promise<void> {
    if (savedIds.has(exchange.id)) return;
    savedIds.add(exchange.id);

    const request = exchange.request;

    // Body may still be decoding when the exchange is first pushed onto the list.
    // Same wait the HAR export and the snapshot button use.
    await request.body.waitForDecoding().catch(() => {});

    let requestBody: string | null = null;
    if (request.body.isDecoded() && request.body.decodedData) {
        const buf = request.body.decodedData;
        try {
            requestBody = UTF8Decoder.decode(buf);
        } catch {
            requestBody = buf.toString('base64');
        }
    }

    const timestamp = new Date().toISOString();
    const filenameTs = timestamp.replace(/[:.]/g, '-');
    const filename =
        `${filenameTs}_${filenameSafe(request.method || 'REQ', 10)}` +
        `_${filenameSafe(request.parsedUrl.host || 'unknown')}` +
        `_${filenameSafe(exchange.id.slice(0, 8))}.json`;

    const payload = JSON.stringify({
        timestamp,
        url: request.parsedUrl.toString(),
        requestHeaders: request.headers,
        requestBody
    }, null, 2);

    try {
        await DesktopApi.writeJsonFile!(folder, filename, payload);
    } catch (e) {
        logError(e as Error);
    }
}

/**
 * Attaches a mobx observer to eventsStore.exchanges. Whenever a new HTTP exchange
 * is pushed, if uiStore.autoSaveEnabled is true and the exchange matches the
 * current activeFilterSet, it is saved to uiStore.autoSaveFolder as a JSON file.
 *
 * Filter binding is live — changing the filter box takes effect immediately.
 *
 * Returns a disposer (unused in practice, since we install for the app's lifetime).
 */
export function installAutoSave(
    uiStore: UiStore,
    eventsStore: EventsStore
): Lambda {
    // The public `exchanges` accessor returns `ReadonlyArray<HttpExchange>`, but the
    // underlying value is a mobx observable.array. We cast so TS picks the array-observer
    // overload (which fires 'splice' changes carrying `added`), not the object-observer
    // one (which fires 'add' | 'update' | 'remove' with no bulk-added array).
    const observableExchanges = eventsStore.exchanges as IObservableArray<HttpExchange>;

    return observe(observableExchanges, (change) => {
        if (change.type !== 'splice') return;
        if (!uiStore.autoSaveEnabled) return;
        if (!uiStore.autoSaveFolder) return;
        if (!DesktopApi.writeJsonFile) return;

        const folder = uiStore.autoSaveFolder;
        const filterSet = uiStore.activeFilterSet;

        for (const exchange of change.added) {
            if (matchesFilter(exchange, filterSet)) {
                // Fire-and-forget — we must not block the mobx observer,
                // and one slow save should not delay others.
                void saveExchange(folder, exchange);
            }
        }
    });
}
